'use client';

import { AiSearchLoading } from '@/new_components/searchresults/AiSearchLoading';
import { AiSearchBanner } from '@/new_components/searchresults/AiSearchBanner';
import { ActiveFilterBar } from '@/new_components/searchresults/ActiveFilterBar';
import CompanyResultsTable from '@/new_components/searchresults/CompanyResultsTable';
import { CompanyPreviewPanel } from '@/new_components/searchresults/CompanyPreviewPanel';
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { CompanySearchResponse, CompanySearchResult } from '@/lib/actions/company-search';
import type { AiSearchProgress } from '@/lib/ai-search-progress';
import type { FilterParams } from '@/lib/search-filter-params';
import type { FilterTier } from '@/lib/search-gating';
import type { useSession } from '@/hooks/use-session';

interface SearchResultsMainProps {
  t: TFunction<'translation', undefined>;
  resultsHeader: ReactNode;
  aiIsLoading: boolean;
  aiProgress: AiSearchProgress;
  listSlug: string | undefined;
  initialFilters: FilterParams;
  tier: FilterTier;
  handleSubmit: (filters: FilterParams) => void;
  previewEuId: string | null;
  openPreview: (euId: string, trigger?: HTMLElement) => void;
  searchResponse: CompanySearchResponse;
  currentOffset: number;
  currentLimit: number;
  currentSort: string;
  handleSortChange: (newSort: string) => void;
  handleOffsetChange: (newOffset: number) => void;
  handleLimitChange: (newLimit: number) => void;
  handleRefresh: () => void;
  currentFinancialYear: number | undefined;
  isProOrAdmin: boolean;
  session: ReturnType<typeof useSession>['data'];
  handleFetchForExport: (limit: number) => Promise<CompanySearchResult[]>;
  logExport: (stage: 'click' | 'completed', extra?: Record<string, unknown>) => void;
  handleEmployeeCountsUpdated: (counts: Record<string, number>) => void;
  isLoading: boolean;
  closePreview: () => void;
}

export function SearchResultsMain({
  t,
  resultsHeader,
  aiIsLoading,
  aiProgress,
  listSlug,
  initialFilters,
  tier,
  handleSubmit,
  previewEuId,
  openPreview,
  searchResponse,
  currentOffset,
  currentLimit,
  currentSort,
  handleSortChange,
  handleOffsetChange,
  handleLimitChange,
  handleRefresh,
  currentFinancialYear,
  isProOrAdmin,
  session,
  handleFetchForExport,
  logExport,
  handleEmployeeCountsUpdated,
  isLoading,
  closePreview,
}: SearchResultsMainProps) {
  return (
<main
        aria-label={t('search.sidebar.title')}
        aria-live="polite"
        className="min-h-[70vh] lg:flex-1 lg:min-h-0 min-w-0 flex flex-col relative lg:overflow-hidden"
      >
        {resultsHeader}
        {aiIsLoading && (
          <div role="progressbar" aria-label={t(aiProgress.phase === 'interpreting' ? 'search.ai.interpreting' : 'search.ai.loadingResults')} className="pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary/20">
            <div className="h-full w-full bg-primary motion-safe:animate-pulse" />
          </div>
        )}
        {aiIsLoading ? <AiSearchLoading key={aiProgress.startedAt} progress={aiProgress} /> : (
          <>
            {!listSlug && <AiSearchBanner />}
            <ActiveFilterBar filters={initialFilters} tier={tier} onChange={handleSubmit} />
            <CompanyResultsTable
              previewEuId={previewEuId}
              onOpenPreview={openPreview}
              results={searchResponse.results}
              total={searchResponse.total}
              totalCapped={searchResponse.totalCapped ?? false}
              offset={currentOffset}
              limit={currentLimit}
              sort={currentSort}
              onSortChange={handleSortChange}
              onOffsetChange={handleOffsetChange}
              onLimitChange={handleLimitChange}
              onRefresh={handleRefresh}
              financialYear={currentFinancialYear}
              canExport={isProOrAdmin}
              canEnrich={isProOrAdmin}
              // Column customization is free for any logged-in user. Guests see the
              // button but get an "account required" prompt (so the locked state
              // below only ever fires for guests).
              canCustomizeColumns={!!session}
              fetchResultsForExport={handleFetchForExport}
              onExportClick={() => logExport('click')}
              onExportSuccess={(info) => logExport('completed', info)}
              onEmployeeCountsUpdated={handleEmployeeCountsUpdated}
              isLoading={isLoading}
              // '' is the explicit Global scope; the table only needs to
              // know when it is Swiss, to pick that scope's columns.
              country={initialFilters.country || undefined}
            />
          </>
        )}
        <CompanyPreviewPanel
          previewEuId={previewEuId}
          onClose={closePreview}
          results={searchResponse.results}
        />
      </main>
  );
}
